class BaseformVideoShowcase extends HTMLElement {
  connectedCallback() {
    this.ensureEnhancementStyles();

    this.rail = this.querySelector('[data-bf-video-rail]');
    this.prev = this.querySelector('[data-bf-video-prev]');
    this.next = this.querySelector('[data-bf-video-next]');
    this.progress = this.querySelector('[data-bf-video-progress]');
    this.modal = this.querySelector('[data-bf-video-modal]');
    this.frame = this.querySelector('[data-bf-video-frame]');

    this.querySelectorAll('[data-bf-video-open]').forEach((button) => {
      button.addEventListener('click', (event) => this.openVideo(event, button));
    });
    this.querySelector('[data-bf-video-close]')?.addEventListener('click', () => this.closeVideo());
    this.modal?.addEventListener('click', (event) => {
      if (event.target === this.modal) this.closeVideo();
    });
    this.prev?.addEventListener('click', () => this.scrollByCard(-1));
    this.next?.addEventListener('click', () => this.scrollByCard(1));
    this.rail?.addEventListener('scroll', () => this.updateControls(), { passive: true });

    if (customElements.get('add-to-cart-component')) {
      this.prepareAddToCartAnimations();
    } else {
      customElements.whenDefined('add-to-cart-component').then(() => this.prepareAddToCartAnimations());
    }

    if ('ResizeObserver' in window && this.rail) {
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.rail);
    }
    requestAnimationFrame(() => this.updateControls());
  }

  ensureEnhancementStyles() {
    if (document.getElementById('bf-video-mobile-enhancements')) return;

    const style = document.createElement('style');
    style.id = 'bf-video-mobile-enhancements';
    style.textContent = `
      .bf-video__card {
        scroll-snap-stop: always;
      }

      .bf-video__product-form {
        position: relative;
      }

      .bf-video__rail {
        min-width: 0;
        max-width: 100%;
        overscroll-behavior-x: contain;
      }

      .bf-video__atc {
        position: relative;
        overflow: hidden;
      }

      .bf-video__atc-default {
        transition: opacity .18s ease, transform .24s cubic-bezier(.2,.75,.25,1);
        will-change: opacity, transform;
      }

      .bf-video__atc-added {
        display: flex !important;
        position: absolute;
        inset: 0;
        align-items: center;
        justify-content: center;
        gap: 7px;
        opacity: 0;
        transform: translateY(55%) scale(.88);
        pointer-events: none;
        transition: opacity .18s ease, transform .26s cubic-bezier(.2,.8,.2,1);
        will-change: opacity, transform;
      }

      .bf-video__atc-added::before {
        content: '✓';
        display: grid;
        place-items: center;
        width: 20px;
        height: 20px;
        border: 1.5px solid currentColor;
        border-radius: 50%;
        font-size: 12px;
        line-height: 1;
        font-weight: 800;
      }

      .bf-video__atc[data-added='true'] .bf-video__atc-default {
        opacity: 0;
        transform: translateY(-48%) scale(.94);
      }

      .bf-video__atc[data-added='true'] .bf-video__atc-added {
        opacity: 1;
        transform: translateY(0) scale(1);
        animation: bf-video-added-pop .58s cubic-bezier(.2,.8,.2,1) both;
      }

      @keyframes bf-video-added-pop {
        0% { transform: translateY(50%) scale(.82); }
        55% { transform: translateY(0) scale(1.06); }
        100% { transform: translateY(0) scale(1); }
      }

      @media (max-width: 749px) {
        .bf-video__rail {
          grid-auto-columns: max(var(--bf-video-mobile-card-width, 84%), 88%) !important;
          gap: max(var(--bf-video-gap, 12px), 12px) !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .bf-video__atc-default,
        .bf-video__atc-added {
          transition: none !important;
          animation: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  prepareAddToCartAnimations() {
    this.querySelectorAll('add-to-cart-component').forEach((component) => {
      const button = component.querySelector('.bf-video__atc');
      if (!button || button.dataset.bfAnimationReady === 'true') return;

      button.dataset.bfAnimationReady = 'true';
      component.dataset.addToCartAnimation = 'true';

      const updateMedia = () => {
        const card = button.closest('[data-bf-video-card]');
        const productImage = card?.querySelector('.bf-video__product-image img');
        if (productImage?.currentSrc || productImage?.src) {
          component.dataset.productVariantMedia = productImage.currentSrc || productImage.src;
        }
      };

      updateMedia();
      button.addEventListener('click', (event) => {
        updateMedia();

        if (typeof component.handleClick === 'function') {
          component.handleClick(event);
          return;
        }

        button.dataset.added = 'true';
        window.setTimeout(() => {
          delete button.dataset.added;
        }, 1100);
      });
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    if (this.frame) this.frame.removeAttribute('src');
  }

  embedUrl(rawUrl) {
    try {
      const url = new URL(rawUrl);
      const host = url.hostname.replace(/^www\./, '').toLowerCase();
      if (host !== 'instagram.com' && host !== 'instagr.am') return null;
      const match = url.pathname.match(/^\/(reel|reels|p|tv)\/([^/?#]+)/i);
      if (!match) return null;
      const type = match[1].toLowerCase() === 'reels' ? 'reel' : match[1].toLowerCase();
      return `https://www.instagram.com/${type}/${match[2]}/embed/`;
    } catch (error) {
      return null;
    }
  }

  openVideo(event, button) {
    const embed = this.embedUrl(button.dataset.instagramUrl);
    if (!embed || !this.modal || !this.frame || typeof this.modal.showModal !== 'function') return;
    event.preventDefault();
    this.frame.src = embed;
    this.modal.showModal();
  }

  closeVideo() {
    if (this.modal?.open) this.modal.close();
    if (this.frame) this.frame.removeAttribute('src');
  }

  cardStep() {
    const card = this.rail?.querySelector('[data-bf-video-card]');
    if (!card || !this.rail) return 0;
    const styles = getComputedStyle(this.rail);
    return card.getBoundingClientRect().width + parseFloat(styles.columnGap || styles.gap || 0);
  }

  scrollByCard(direction) {
    if (!this.rail) return;
    this.rail.scrollBy({ left: direction * this.cardStep(), behavior: 'smooth' });
  }

  updateControls() {
    if (!this.rail) return;
    const maxScroll = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
    const current = Math.max(0, Math.min(this.rail.scrollLeft, maxScroll));
    const hasOverflow = maxScroll > 2;
    if (this.prev) this.prev.disabled = !hasOverflow || current <= 2;
    if (this.next) this.next.disabled = !hasOverflow || current >= maxScroll - 2;
    const arrows = this.querySelector('.bf-video__arrows');
    if (arrows) arrows.hidden = !hasOverflow;
    if (this.progress) {
      const visibleRatio = Math.min(1, this.rail.clientWidth / Math.max(this.rail.scrollWidth, 1));
      const travel = Math.max(0, 1 - visibleRatio);
      const scrollRatio = maxScroll > 0 ? current / maxScroll : 0;
      this.progress.style.transform = `translateX(${scrollRatio * travel * 100}%) scaleX(${visibleRatio})`;
    }
  }
}

if (!customElements.get('baseform-video-showcase')) customElements.define('baseform-video-showcase', BaseformVideoShowcase);
